import React, { useRef, useMemo, useEffect } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { scrollState, perf, getNoiseActivity } from "./scrollMath";
import { dustVertex, dustFragment } from "./shaders";

// Pointer in normalized device coords (-1..1), for cursor-repel.
const pointerNDC = { x: -2, y: -2, active: false };

// Persistent RED dust across the whole site: streams with scroll, softly repels the cursor.
function Dust() {
  const pointsRef = useRef();
  const matRef = useRef();
  // Raised from 950: the round mask made each mote read smaller than the old
  // square sprite, so the field looked sparser at the same count.
  const COUNT = 1600;
  const red = useMemo(() => new THREE.Color("#E45F6A"), []);
  const redHot = useMemo(() => new THREE.Color("#ff7d86"), []);
  const cursorWorld = useMemo(() => new THREE.Vector3(9999, 9999, 0), []);
  const tmp = useMemo(() => new THREE.Vector3(), []);

  const { positions, base, scales } = useMemo(() => {
    const positions = new Float32Array(COUNT * 3);
    const base = new Float32Array(COUNT * 3);
    const scales = new Float32Array(COUNT);
    for (let i = 0; i < COUNT; i++) {
      const x = (Math.random() - 0.5) * 13;
      const y = (Math.random() - 0.5) * 8;
      const z = -19 + Math.random() * 26;
      positions[i * 3] = x; positions[i * 3 + 1] = y; positions[i * 3 + 2] = z;
      base[i * 3] = x; base[i * 3 + 1] = y; base[i * 3 + 2] = z;
      // Skew small: mostly fine motes, a few larger ones catching the light.
      const r = Math.random();
      scales[i] = 0.45 + r * r * 1.15;
    }
    return { positions, base, scales };
  }, []);

  const uniforms = useMemo(
    () => ({
      uColor: { value: new THREE.Color("#E45F6A") },
      uOpacity: { value: 0.8 },
      uSize: { value: 0.075 },
      uScale: { value: 400 },
    }),
    []
  );

  useEffect(() => {
    const onMove = (e) => {
      pointerNDC.x = (e.clientX / window.innerWidth) * 2 - 1;
      pointerNDC.y = -((e.clientY / window.innerHeight) * 2 - 1);
      pointerNDC.active = true;
    };
    const onLeave = () => { pointerNDC.active = false; };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerdown", onMove);
    window.addEventListener("pointerleave", onLeave);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onMove);
      window.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  const R = 1.9;      // repel radius (world units)
  const R2 = R * R;
  const STRENGTH = 1.6;
  const EASE = 0.12;

  useFrame((state) => {
    const p = scrollState.progress;
    const activity = getNoiseActivity(p);
    const t = state.clock.elapsedTime;
    const geo = pointsRef.current.geometry;
    const arr = geo.attributes.position.array;

    // Project cursor onto the z=0 plane in world space.
    if (pointerNDC.active) {
      tmp.set(pointerNDC.x, pointerNDC.y, 0.5).unproject(state.camera);
      tmp.sub(state.camera.position).normalize();
      const dist = (0 - state.camera.position.z) / tmp.z;
      cursorWorld.copy(state.camera.position).add(tmp.multiplyScalar(dist));
    } else {
      cursorWorld.set(9999, 9999, 0);
    }

    for (let i = 0; i < COUNT; i++) {
      const ix = i * 3;
      // home position: base + gentle wobble + scroll-streamed z
      const homeX = base[ix] + Math.sin(t * 0.4 + i) * 0.08;
      const homeY = base[ix + 1] + Math.cos(t * 0.33 + i) * 0.08;
      let homeZ = base[ix + 2] + p * 34 + t * 0.15;
      homeZ = ((homeZ + 19) % 26) - 19;

      let targetX = homeX;
      let targetY = homeY;

      // cursor repel (in XY)
      const dx = arr[ix] - cursorWorld.x;
      const dy = arr[ix + 1] - cursorWorld.y;
      const d2 = dx * dx + dy * dy;
      if (d2 < R2 && d2 > 1e-4) {
        const d = Math.sqrt(d2);
        const f = 1 - d / R;
        const push = f * f * STRENGTH;
        targetX += (dx / d) * push;
        targetY += (dy / d) * push;
      }

      arr[ix] += (targetX - arr[ix]) * EASE;
      arr[ix + 1] += (targetY - arr[ix + 1]) * EASE;
      arr[ix + 2] = homeZ;
    }
    geo.attributes.position.needsUpdate = true;
    geo.setDrawRange(0, Math.floor(COUNT * perf.quality));

    const u = matRef.current.uniforms;
    u.uOpacity.value = 0.8 + activity * 0.2;
    u.uColor.value.copy(red).lerp(redHot, activity);
    u.uSize.value = 0.075 + activity * 0.03;
    // Matches PointsMaterial's sizeAttenuation, so the motes keep their
    // apparent size when the window resizes.
    u.uScale.value = state.size.height * 0.5;
  });

  return (
    <points ref={pointsRef} frustumCulled={false}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={COUNT} array={positions} itemSize={3} />
        <bufferAttribute attach="attributes-aScale" count={COUNT} array={scales} itemSize={1} />
      </bufferGeometry>
      <shaderMaterial
        ref={matRef}
        uniforms={uniforms}
        vertexShader={dustVertex}
        fragmentShader={dustFragment}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

export default function NoiseField() {
  return <Dust />;
}
